import styled from '@emotion/styled';
import { captureException } from '@sentry/browser';
import React, { useEffect } from 'react';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import image from 'src/public/img/marginalia-done.svg';

import { appClient } from 'src/config/apollo';
import { Dispatch } from 'src/config/rootStore';
import { CareRecommendationNode } from 'src/generated/webapp_gql';
import { getWho5Score } from 'src/modules/assessments/selectors/assessmentsSelectors';
import { BrandWrapper } from 'src/modules/onboarding/components/BrandWrapper';
import { useStartCareRecommendationMutation } from 'src/modules/onboarding/documents/startCareRecommendationMutation.generated';
import { Button } from 'src/shared/components/Button';
import { color, font } from 'src/shared/styles/variables';

import { ReTriageScoreText } from '../components/ReTriageScoreText';

const Wrapper = styled.div({
    width: 600,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    paddingBottom: 100,
});

const TextsWrapper = styled.div({
    maxWidth: 360,
    width: '100%',
});

const HeaderText = styled.h3({
    color: color.ebonyClay,
    marginBottom: 20,
    textAlign: 'center',
    ...font.black(font.size.extraLarge),
});

const DescriptionText = styled.p({
    color: color.blueBayoux,
    marginBottom: 16,
    lineHeight: '26px',
    textAlign: 'center',
    ...font.book(font.size.medium),
});

const Image = styled.img({
    width: 250,
});

const ButtonsWrapper = styled.div({
    maxWidth: 500,
    width: '100%',
    marginTop: 50,
    display: 'flex',
    justifyContent: 'space-between',
});

type Props = RouteComponentProps<{}, {}, { previousScore: number }>;

export const ReTriageGreenYellowSummaryPage: React.FC<Props> = ({ history, location }) => {
    const { t } = useTranslation();
    const currentScore = useSelector(getWho5Score);

    const [startCareRecommendation] = useStartCareRecommendationMutation({
        client: appClient,
    });

    const {
        user: { addCareRecommendation },
    } = useDispatch<Dispatch>();

    useEffect(() => {
        ReactGA.event({
            category: 'Re-triage flow',
            action: 'Visited Care Advisor page',
            label: 'retriage_ca_summary_visited',
        });

        startCareRecommendation({ variables: { onboarding: false } })
            .then(response => {
                if (response?.data?.startCareRecommendation) {
                    addCareRecommendation(
                        response.data.startCareRecommendation
                            .careRecommendation as CareRecommendationNode,
                    );
                }
            })
            .catch(captureException);
    }, []);

    const handleNotNowClick = () => {
        ReactGA.event({
            category: 'Re-triage flow',
            action: 'Not now clicked on Care Advisor page',
            label: 'retriage_not_now_clicked',
        });

        history.replace('/dashboard');
    };

    const handleRequestClick = () => {
        history.push('/re-triage-care-advisor');

        ReactGA.event({
            category: 'Re-triage flow',
            action: 'Request button clicked on Care Advisor page',
            label: 'retriage_request_ca_clicked',
        });
    };

    const previousScore = location.state?.previousScore;

    return (
        <BrandWrapper>
            <Wrapper>
                <Image src={image} />

                <TextsWrapper>
                    <HeaderText>{t('retriage:summary_page.title')}</HeaderText>
                    <ReTriageScoreText currentScore={currentScore} previousScore={previousScore} />
                    <DescriptionText>{t('retriage:summary_page.description.1')}</DescriptionText>
                    <DescriptionText>{t('retriage:summary_page.description.2')}</DescriptionText>
                </TextsWrapper>

                <ButtonsWrapper>
                    <Button
                        onClick={handleNotNowClick}
                        widthPercentage={48.5}
                        type="white"
                        noHorizontalPadding
                    >
                        {t('common.not_now')}
                    </Button>
                    <Button onClick={handleRequestClick} widthPercentage={48.5} noHorizontalPadding>
                        {t('retriage:summary_page.request_care')}
                    </Button>
                </ButtonsWrapper>
            </Wrapper>
        </BrandWrapper>
    );
};
